/**
 * AudioManager — slice 2 of the audio runtime (docs/design/18-audio.md).
 *
 * A thin wrapper over Howler that reads the shared sound registry and owns:
 *   - lazy Howl construction per manifest key (one Howl, reused across plays),
 *   - bus volumes + master mute (persisted), applied live to looping beds,
 *   - the autoplay-unlock dance + iOS resume-on-focus,
 *   - positional SFX (equal-power panner, listener = camera target),
 *   - per-id polyphony caps, cooldowns, pitch jitter, variant rotation,
 *   - the missing-file no-op guard (an unresolved key never constructs a Howl).
 *
 * Public surface mirrors the spec: play / loop / stop / setBusVolume / muteBus /
 * setMasterMute / setListener / unlock.
 *
 * Design note (deviation from the spec's "GainNode-per-bus"): rather than patch
 * Howler's internal node graph (fragile across versions), bus + master gain are
 * folded into each Howl's `volume()`. Because `Howl.volume()` on an already-
 * playing instance updates live, `setBusVolume` still affects in-flight music/
 * ambience loops — the property the GainNode approach existed to guarantee. SFX
 * one-shots are too short to bother re-voluming mid-play.
 */

import { Howl, Howler } from "howler";
import {
  getSound,
  BUS_DEFAULT_VOLUME,
  type AudioBus,
  type SoundDef,
} from "@tribez/shared";
import { resolveAsset, type AudioManifest } from "./manifest";

/**
 * The subset of Howl this manager drives. A seam (over `new Howl`) so the
 * volume-ordering contract can be unit-tested with a fake that records call
 * order — the real bug ("html5 sounds start at full volume") is invisible to a
 * value check; only the *sequence* of group-volume vs. play() reveals it.
 */
export interface HowlLike {
  play(): number;
  loop(loop: boolean, id?: number): void;
  // Howler overloads volume(): group get; volume(v): group set; volume(v,id):
  // instance set; volume(id): instance get. We model that loosely.
  volume(...args: number[]): number | HowlLike;
  fade(from: number, to: number, dur: number, id?: number): void;
  pos(x: number, y: number, z: number, id?: number): void;
  pannerAttr(attr: Record<string, unknown>, id?: number): void;
  rate(rate: number, id?: number): void;
  stop(id?: number): void;
  once(event: string, cb: () => void, id?: number): void;
}

export interface HowlConfig {
  src: string[];
  format: string[];
  html5: boolean;
  loop: boolean;
  preload: boolean;
  /** Initial GROUP volume — see getHowl(); load-bearing for the html5 cold-start. */
  volume: number;
}

export type HowlFactory = (config: HowlConfig) => HowlLike;

const defaultHowlFactory: HowlFactory = (config) =>
  new Howl(config) as unknown as HowlLike;

export interface PlayOptions {
  /** World XZ for spatial sounds; ignored for flat sounds. */
  position?: { x: number; z: number };
  /** Extra ± semitone offset on top of the def's pitchVariance (one-off). */
  detune?: number;
}

export interface LoopOptions {
  fadeMs?: number;
  position?: { x: number; z: number };
}

// Inverse falloff (not linear): full out to refDistance, then a fast 1/d-style
// roll-off so a sound's *distance* is audible across the visible play area
// instead of the old gentle linear ramp (8→60m) that kept most on-screen action
// near full volume. refDistance ≈ "right next to the hero"; rolloff steepens the
// near-field drop; maxDistance clamps the gain past it (far-map events stay a
// faint background rather than vanishing abruptly). Panning is equal-power L/R,
// keyed to the listener orientation (set per-frame from the camera).
const PANNER_ATTR = {
  panningModel: "equalpower",
  distanceModel: "inverse",
  refDistance: 6,
  maxDistance: 55,
  rolloffFactor: 1.3,
} as const;

const MUTE_STORAGE_KEY = "tribez:audio:master-mute";
const BUS_VOLUME_STORAGE_KEY = "tribez:audio:bus-volume";
const STREAM_BUSES: ReadonlySet<AudioBus> = new Set<AudioBus>(["music", "ambience"]);

/** semitones → playbackRate multiplier. */
function semitonesToRate(semitones: number): number {
  return Math.pow(2, semitones / 12);
}

interface LoopHandle {
  id: string;
  howl: HowlLike;
  soundId: number;
  /** Raw def volume (0..1), pre-bus. Effective = rawVolume × bus mul. */
  rawVolume: number;
  bus: AudioBus;
}

export class AudioManager {
  private readonly howls = new Map<string, HowlLike>(); // assetKey → Howl (lazy)
  private readonly busVolume: Record<AudioBus, number>;
  private readonly busMuted: Record<AudioBus, boolean> = {
    music: false, ambience: false, sfx: false, ui: false, voice: false,
  };
  private masterMuted = false;
  private unlocked = false;
  /** id → last play timestamp (performance.now ms) for cooldown gating. */
  private readonly lastPlayedAt = new Map<string, number>();
  /** id → live Howler instance ids, for per-id polyphony caps. */
  private readonly active = new Map<string, number[]>();
  /** id → looping bed handle (ambience/music/channel). */
  private readonly loops = new Map<string, LoopHandle>();

  /** QA-only: total play() calls per id (incl. silent/missing), for the
   *  game-qa introspection handle — audio can't be screenshotted. */
  private readonly playCounts = new Map<string, number>();
  private lastPlayed = "";

  constructor(
    private readonly manifest: AudioManifest,
    private readonly createHowl: HowlFactory = defaultHowlFactory,
  ) {
    this.busVolume = { ...BUS_DEFAULT_VOLUME };
    try {
      this.masterMuted = localStorage.getItem(MUTE_STORAGE_KEY) === "1";
      const raw = localStorage.getItem(BUS_VOLUME_STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as Partial<Record<AudioBus, number>>;
        for (const bus of Object.keys(this.busVolume) as AudioBus[]) {
          const v = saved[bus];
          if (typeof v === "number" && v >= 0 && v <= 1) this.busVolume[bus] = v;
        }
      }
    } catch { /* private-mode / no storage / bad JSON → defaults */ }
    Howler.mute(this.masterMuted);
    // iOS suspends the shared AudioContext on tab-blur; resume on return.
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) this.resumeContext();
    });
    this.installQaHandle();
  }

  /** Read-only snapshot for `game/qa` — never read by production code. */
  private installQaHandle(): void {
    (window as unknown as { __audio?: { snapshot: () => unknown } }).__audio = {
      snapshot: () => ({
        unlocked: this.unlocked,
        masterMuted: this.masterMuted,
        ctxState: Howler.ctx?.state,
        busVolume: { ...this.busVolume },
        loadedHowls: this.howls.size,
        activeLoops: Array.from(this.loops.keys()),
        lastPlayed: this.lastPlayed,
        playCounts: Object.fromEntries(this.playCounts),
      }),
    };
  }

  // --- unlock ---------------------------------------------------------------

  /** Bind to the landing "Play" gesture — browsers gate audio until then. */
  unlock(): void {
    this.unlocked = true;
    this.resumeContext();
  }

  private resumeContext(): void {
    const ctx = Howler.ctx;
    if (ctx && ctx.state === "suspended") void ctx.resume();
  }

  // --- resolution / construction -------------------------------------------

  /** Get or lazily build the Howl for a manifest key. Returns undefined when
   *  the key has no asset (missing-file no-op).
   *
   *  `initialVolume` seeds the Howl's GROUP volume at construction. This is
   *  load-bearing for html5 (music/ambience) sounds: Howler creates each play
   *  instance with `sound._volume = group._volume` and, for html5, sets the
   *  `<audio>` element's volume from that at play() time (howler.js 2.2.4
   *  L902/L2221). If the group default (1.0) is left in place and we only set
   *  the per-instance volume *after* play(), the stream cold-starts at full
   *  volume for a beat before the (queued, see L1239) correction lands — the
   *  "sound suddenly gets loud" bug. Seeding the group volume up front fixes
   *  the FIRST play of a freshly-built Howl (before it's `loaded`, when a
   *  post-construction volume() call would be queued instead of applied). */
  private getHowl(
    assetKey: string,
    bus: AudioBus,
    loop: boolean,
    initialVolume: number,
  ): HowlLike | undefined {
    let howl = this.howls.get(assetKey);
    if (howl) return howl;
    const resolved = resolveAsset(this.manifest, assetKey);
    if (!resolved) return undefined; // silent no-op
    howl = this.createHowl({
      src: resolved.urls,
      format: resolved.formats,
      // Music/ambience stream (no full decode of long beds); SFX use Web Audio
      // (required for positional panning).
      html5: STREAM_BUSES.has(bus),
      loop,
      preload: true,
      volume: initialVolume,
    });
    this.howls.set(assetKey, howl);
    return howl;
  }

  private effectiveVolume(def: SoundDef): number {
    const base = def.volume ?? 1;
    const busMul = this.busMuted[def.bus] ? 0 : this.busVolume[def.bus];
    return base * busMul;
  }

  // --- one-shots ------------------------------------------------------------

  play(id: string, opts: PlayOptions = {}): void {
    const def = getSound(id);
    this.playCounts.set(id, (this.playCounts.get(id) ?? 0) + 1);
    this.lastPlayed = id;
    if (!def) return;

    // Cooldown gate (de-machine-gun rapid triggers).
    const now = performance.now();
    if (def.cooldownMs) {
      const last = this.lastPlayedAt.get(id);
      if (last !== undefined && now - last < def.cooldownMs) return;
    }

    // Pick the asset key: random variant, else explicit asset, else the id.
    const assetKey = def.variants?.length
      ? def.variants[Math.floor(Math.random() * def.variants.length)]!
      : def.asset ?? id;

    const target = this.effectiveVolume(def);
    const howl = this.getHowl(assetKey, def.bus, def.loop ?? false, target);
    if (!howl) return; // missing file → silent

    // Seed the GROUP volume *before* play() so the instance Howler creates inside
    // play() cold-starts at the right level — critical for html5 music/ambience,
    // which set the <audio> element's volume from the group volume at play() time
    // (a post-play per-instance set arrives a beat late → audible full-volume
    // blip). On a cache-hit this also re-applies the current bus mix. The
    // per-instance set below then pins the exact value (a no-op when equal).
    howl.volume(target);
    const soundId = howl.play();
    this.lastPlayedAt.set(id, now);
    howl.volume(target, soundId);

    // Pitch jitter (short SFX only — never rhythmic/looped).
    const jitter = def.pitchVariance ?? 0;
    const detune = (opts.detune ?? 0) + (jitter ? (Math.random() * 2 - 1) * jitter : 0);
    if (detune) howl.rate(semitonesToRate(detune), soundId);

    // Spatialization.
    if (def.spatial && opts.position) {
      howl.pannerAttr(PANNER_ATTR, soundId);
      howl.pos(opts.position.x, 0, opts.position.z, soundId);
    }

    // Polyphony cap (steal oldest beyond maxConcurrent).
    if (def.maxConcurrent) {
      const list = this.active.get(id) ?? [];
      list.push(soundId);
      while (list.length > def.maxConcurrent) {
        const oldest = list.shift();
        if (oldest !== undefined) howl.stop(oldest);
      }
      this.active.set(id, list);
      howl.once("end", () => {
        const cur = this.active.get(id);
        if (cur) {
          const idx = cur.indexOf(soundId);
          if (idx >= 0) cur.splice(idx, 1);
        }
      }, soundId);
    }
  }

  // --- loops (ambience / music / channel) -----------------------------------

  loop(id: string, opts: LoopOptions = {}): void {
    if (this.loops.has(id)) return; // already running
    const def = getSound(id);
    if (!def) return;
    const assetKey = def.asset ?? id;
    const rawVolume = def.volume ?? 1;
    const target = this.effectiveVolume(def);
    const fadeIn = !!(opts.fadeMs && opts.fadeMs > 0);

    // Seed the GROUP volume before play() (see play() / getHowl()): fade-ins must
    // cold-start silent and ramp up; instant loops start at the effective level.
    // Without this the looping bed blasts at full volume for a beat on every
    // crossfade / day-night edge before settling.
    const howl = this.getHowl(assetKey, def.bus, true, fadeIn ? 0 : target);
    if (!howl) return;

    howl.volume(fadeIn ? 0 : target);
    const soundId = howl.play();
    howl.loop(true, soundId);

    if (def.spatial && opts.position) {
      howl.pannerAttr(PANNER_ATTR, soundId);
      howl.pos(opts.position.x, 0, opts.position.z, soundId);
    }

    if (fadeIn) {
      howl.volume(0, soundId);
      howl.fade(0, target, opts.fadeMs!, soundId);
    } else {
      howl.volume(target, soundId);
    }
    this.loops.set(id, { id, howl, soundId, rawVolume, bus: def.bus });
  }

  stop(id: string, opts: { fadeMs?: number } = {}): void {
    const handle = this.loops.get(id);
    if (!handle) return;
    this.loops.delete(id);
    const { howl, soundId } = handle;
    if (opts.fadeMs && opts.fadeMs > 0) {
      howl.fade(howl.volume(soundId) as number, 0, opts.fadeMs, soundId);
      howl.once("fade", () => howl.stop(soundId), soundId);
    } else {
      howl.stop(soundId);
    }
  }

  isLooping(id: string): boolean {
    return this.loops.has(id);
  }

  // --- mixing ---------------------------------------------------------------

  setBusVolume(bus: AudioBus, volume: number): void {
    this.busVolume[bus] = Math.max(0, Math.min(1, volume));
    this.applyBusVolumeToLoops(bus);
    try { localStorage.setItem(BUS_VOLUME_STORAGE_KEY, JSON.stringify(this.busVolume)); } catch { /* ignore */ }
  }

  getBusVolume(bus: AudioBus): number {
    return this.busVolume[bus];
  }

  muteBus(bus: AudioBus, muted: boolean): void {
    this.busMuted[bus] = muted;
    this.applyBusVolumeToLoops(bus);
  }

  private applyBusVolumeToLoops(bus: AudioBus): void {
    const mul = this.busMuted[bus] ? 0 : this.busVolume[bus];
    this.loops.forEach((h) => {
      if (h.bus !== bus) return;
      h.howl.volume(h.rawVolume * mul, h.soundId);
    });
  }

  setMasterMute(muted: boolean): void {
    this.masterMuted = muted;
    Howler.mute(muted);
    try { localStorage.setItem(MUTE_STORAGE_KEY, muted ? "1" : "0"); } catch { /* ignore */ }
  }

  isMasterMuted(): boolean {
    return this.masterMuted;
  }

  // --- listener -------------------------------------------------------------

  /**
   * Called each frame with the listening point (the camera's orbit target) and
   * the camera's horizontal facing. Orientation is what makes panning
   * *directional*: without it the listener keeps Web Audio's default forward
   * (−z), so L/R panning ignores the camera's facing — so we feed the camera's
   * ground-plane forward each frame. (The azimuth is now fixed, so this forward
   * is effectively constant, but feeding it per-frame stays correct regardless.)
   * `forward` need not be normalized; up is world +y.
   */
  setListener(pos: { x: number; z: number }, forward?: { x: number; z: number }): void {
    Howler.pos(pos.x, 0, pos.z);
    if (forward && (forward.x !== 0 || forward.z !== 0)) {
      Howler.orientation(forward.x, 0, forward.z, 0, 1, 0);
    }
  }
}
